// Web Speech API STT wrapper with PTT semantics.
// Probes capability at construction; the consumer calls start/stop on user gesture.

type SpeechRecognitionResultEv = {
  results: { [k: number]: { [k: number]: { transcript: string; confidence: number } } & { isFinal: boolean }; length: number };
};

export interface RecognizerEvents {
  onResult: (text: string, confidence: number, isFinal: boolean) => void;
  onError?: (err: string) => void;
  onEnd?: () => void;
}

interface SR {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionResultEv) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition?: { new(): SR };
    webkitSpeechRecognition?: { new(): SR };
  }
}

export class VoiceRecognizer {
  available: boolean;
  isListening = false;
  private rec: SR | null = null;
  private events: RecognizerEvents | null = null;

  constructor() {
    const Cls = window.SpeechRecognition ?? window.webkitSpeechRecognition;
    this.available = typeof Cls === 'function';
    if (Cls) {
      const r = new Cls();
      r.lang = 'zh-CN';
      r.continuous = false;
      r.interimResults = true;
      r.maxAlternatives = 1;
      r.onresult = (e) => {
        const last = e.results[e.results.length - 1];
        const alt = last[0];
        const text = (alt.transcript || '').trim();
        if (!text) return;
        this.events?.onResult(text, alt.confidence ?? 0, last.isFinal);
      };
      r.onerror = (e) => { this.events?.onError?.(e.error); };
      r.onend = () => { this.isListening = false; this.events?.onEnd?.(); };
      this.rec = r;
    }
  }

  start(events: RecognizerEvents): boolean {
    if (!this.rec) return false;
    if (this.isListening) return true;
    this.events = events;
    try {
      this.rec.start();
      this.isListening = true;
      return true;
    } catch {
      this.isListening = false;
      return false;
    }
  }

  stop(): void {
    if (!this.rec || !this.isListening) return;
    try { this.rec.stop(); } catch { /* ignore */ }
    this.isListening = false;
  }
}
